'use client';

import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import styles from './website-rotating-headline.module.css';

export function WebsiteRotatingHeadline() {
  const { t } = useTranslation('marketing');
  const words = ['business', 'website', 'support', 'chat', 'team'].map((key) =>
    t(`rotatingHeadline.${key}`),
  );
  const wordKey = words.join('|');
  const node = useRef<HTMLSpanElement>(null);
  const elapsed = useRef(0);
  const [value, setValue] = useState(words[0]);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    const items = wordKey.split('|');
    const media = matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false;
    let frame = 0;
    let previous = 0;
    const lengths = items.map(
      (word) => word.length * 80 + 1900 + word.length * 45 + 300,
    );
    const total = lengths.reduce((a, b) => a + b, 0);
    const tick = (now: number) => {
      if (previous) elapsed.current += Math.min(now - previous, 100);
      previous = now;
      let at = elapsed.current % total;
      let index = 0;
      while (index < lengths.length - 1 && at >= lengths[index]!) {
        at -= lengths[index]!;
        index++;
      }
      const word = items[index]!;
      const typed = word.length * 80;
      const count =
        at < typed
          ? Math.floor(at / 80)
          : at < typed + 1900
            ? word.length
            : Math.max(0, word.length - Math.floor((at - typed - 1900) / 45));
      setValue(word.slice(0, count));
      frame = requestAnimationFrame(tick);
    };
    const sync = () => {
      cancelAnimationFrame(frame);
      previous = 0;
      if (media.matches) setValue(items[0]);
      else if (visible && !document.hidden && !paused)
        frame = requestAnimationFrame(tick);
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      sync();
    });
    if (node.current) observer.observe(node.current);
    media.addEventListener('change', sync);
    document.addEventListener('visibilitychange', sync);
    sync();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      media.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, [wordKey, paused]);
  return (
    <span ref={node} className={styles.layout}>
      {words.map((word, index) => (
        <span key={index} className={styles.heightReserve} aria-hidden="true">
          {t('rotatingHeadline.before')}{' '}
          <span className={styles.word}>
            <span className={styles.typed}>
              {word}
              <span className={styles.caret} />
            </span>
          </span>{' '}
          {t('rotatingHeadline.after')}
        </span>
      ))}
      <span className={styles.visible}>
        <span className="sr-only">{t('home.title')}</span>
        <span aria-hidden="true">{t('rotatingHeadline.before')} </span>
        <button
          type="button"
          className={styles.word}
          aria-label={t('rotatingHeadline.pause')}
          aria-pressed={paused}
          onClick={() => setPaused(!paused)}
        >
          <span className={styles.typed} aria-hidden="true">
            {value}
            <span className={styles.caret} />
          </span>
        </button>{' '}
        <span aria-hidden="true">{t('rotatingHeadline.after')}</span>
      </span>
    </span>
  );
}
